/**
 * PromptTemplateField の編集 UI。
 * D&D 並び替えは HTML5 native draggable で実装。
 */
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { PromptFieldType, PromptTemplateField } from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface FieldEditorProps {
  fields: PromptTemplateField[];
  onChange: (fields: PromptTemplateField[]) => void;
}

function genFieldId(): string {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

export function FieldEditor({ fields, onChange }: FieldEditorProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const updateField = (idx: number, patch: Partial<PromptTemplateField>) => {
    const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    onChange(next);
  };

  const removeField = (idx: number) => {
    onChange(fields.filter((_, i) => i !== idx));
  };

  const addField = () => {
    onChange([
      ...fields,
      {
        id: genFieldId(),
        label: '',
        type: 'text',
        placeholder: '',
        options: [],
      },
    ]);
  };

  const handleDragStart = (idx: number) => () => setDragIdx(idx);
  const handleDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...fields];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(idx);
    onChange(next);
  };
  const handleDragEnd = () => setDragIdx(null);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="mb-0">フィールド定義</Label>
        <Button type="button" variant="secondary" size="sm" onClick={addField}>
          <Plus />
          フィールド追加
        </Button>
      </div>

      {fields.length === 0 && (
        <p className="text-xs text-[var(--text-muted)] italic py-4 text-center">
          フィールドが未登録です
        </p>
      )}

      {fields.map((field, idx) => (
        <div
          key={field.id}
          draggable
          onDragStart={handleDragStart(idx)}
          onDragOver={handleDragOver(idx)}
          onDragEnd={handleDragEnd}
          className={`rounded bg-[var(--bg-surface)] border border-[var(--border)] p-3 ${
            dragIdx === idx ? 'opacity-50' : ''
          }`}
        >
          <div className="flex items-start gap-2">
            <div className="cursor-grab pt-2 text-[var(--text-muted)]">
              <GripVertical className="size-4" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor={`${field.id}-label`} className="text-xs">
                    ラベル
                  </Label>
                  <Input
                    id={`${field.id}-label`}
                    value={field.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    placeholder="例: 件名の主旨"
                  />
                </div>
                <div>
                  <Label className="text-xs">入力タイプ</Label>
                  <Select
                    value={field.type}
                    onValueChange={(v) =>
                      updateField(idx, {
                        type: v as PromptFieldType,
                        options: v === 'select' ? field.options : [],
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">text（1 行）</SelectItem>
                      <SelectItem value="textarea">textarea（複数行）</SelectItem>
                      <SelectItem value="select">select（選択肢）</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor={`${field.id}-placeholder`} className="text-xs">
                    プレースホルダ
                  </Label>
                  <Input
                    id={`${field.id}-placeholder`}
                    value={field.placeholder}
                    onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                    placeholder="例: 春の新商品"
                  />
                </div>
              </div>
              {field.type === 'select' && (
                <div>
                  <Label className="text-xs">選択肢（改行区切り）</Label>
                  <textarea
                    value={field.options.join('\n')}
                    onChange={(e) =>
                      updateField(idx, {
                        options: e.target.value.split('\n').map((o) => o.trim()).filter(Boolean),
                      })
                    }
                    className="w-full min-h-[60px] rounded border border-[var(--border)] bg-[var(--bg-input)] p-2 text-[13px]"
                    placeholder="選択肢A&#10;選択肢B&#10;選択肢C"
                  />
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeField(idx)}
              title="フィールドを削除"
            >
              <Trash2 />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
